<template>
        <section v-if="overview && access" class="reader-book-overview reader-book-overview--web" :aria-label="overview.overviewLabel">
                <div class="content-fon">
                        <div class="content">
                                <div class="intro">
                                        <div class="background" aria-hidden="true"></div>
                                        <div class="cover">
                                                <img class="image" :src="bookManifest.coverSrc" :alt="bookManifest.coverAlt" />
                                        </div>

                                        <div class="details">
                                                <p class="eyebrow">ОБ УЧЕБНИКЕ</p>
                                                <h1>{{ overview.title }}</h1>
                                                <p class="level">{{ overview.educationLevel }}</p>
                                                <p class="authors"><strong>{{ overview.authorsLabel }}</strong> {{ overview.authors }}</p>
                                                <p class="description">{{ overview.description }}</p>
                                                <div v-if="isDescriptionExpanded" class="description extra">
                                                        <div v-for="paragraph in overview.extendedDescription" :key="paragraph">{{ paragraph }}</div>
                                                </div>
                                                <a class="more" href="#" :aria-expanded="isDescriptionExpanded" @click.prevent="isDescriptionExpanded = !isDescriptionExpanded">{{ isDescriptionExpanded ? overview.hideLabel : overview.moreLabel }}</a>
                                                <div class="tags">
                                                        <span v-for="tag in overview.tags" :key="tag">{{ tag }}</span>
                                                        <span>{{ totalPages }} {{ overview.pageCountLabel }}</span>
                                                </div>
                                        </div>

                                        <aside class="access" :class="{ resume: hasReadingProgress }" aria-label="Начало работы с учебником">
                                                <p class="eyebrow">{{ access.eyebrow }}</p>
                                                <h2>{{ access.title }}</h2>
                                                <p class="description">{{ access.description }}</p>
                                                <div v-if="hasReadingProgress" class="progress">
                                                        <p class="page-number">Стр. {{ currentPageNumber }} из {{ readingPageCount }}</p>
                                                        <div class="track" role="progressbar" aria-label="Прогресс чтения" aria-valuemin="0" :aria-valuenow="currentPageNumber ?? 0" :aria-valuemax="readingPageCount">
                                                                <span class="fill" :style="{ width: `${readingProgress}%` }"></span>
                                                        </div>
                                                </div>
                                                <Button class="open" type="button" :label="access.actionLabel" icon="pi pi-angle-right" icon-pos="right" @click="emit('open-reader')" />
                                                <dl v-if="hasReadingProgress && sessionUser" class="stats">
                                                        <div><dt>Закладки</dt><dd>{{ isLoadingAnnotations || annotationsError ? '—' : bookmarkCount }}</dd></div>
                                                        <div><dt>Заметки</dt><dd>{{ isLoadingAnnotations || annotationsError ? '—' : noteCount }}</dd></div>
                                                </dl>
                                                <p v-if="annotationsError" class="annotation-error" role="alert">{{ annotationsError }}</p>
                                        </aside>
                                </div>

                                <section class="application" aria-labelledby="reader-book-overview-application-title">
                                        <div class="application1">
                                                <h2 id="reader-book-overview-application-title">Установите приложение</h2>
                                                <p class="desc">Установите приложение, чтобы работать с электронным учебником на компьютере, планшете или смартфоне</p>
                                                <ul class="features">
                                                        <li class="offline"><strong>Учебник доступен офлайн</strong><span>Работайте с учебником без подключения к интернету</span></li>
                                                        <li class="functions"><strong>Все основные функции доступны</strong><span>Используйте содержание, поиск, закладки и заметки</span></li>
                                                </ul>
                                        </div>
                                        <div class="platforms">
                                                <h3>ВЫБЕРИТЕ ПЛАТФОРМУ</h3>
                                                <p>Выберите операционную систему, чтобы скачать приложение.</p>
                                                <div class="platform-list">
                                                        <a class="platform" role="link" :href="bookManifest.windowsInstallerSrc || undefined" :aria-disabled="!bookManifest.windowsInstallerSrc" :tabindex="bookManifest.windowsInstallerSrc ? undefined : -1" download aria-label="Скачать приложение для Windows">
                                                                <span class="symbol windows" aria-hidden="true"></span>
                                                        </a>
                                                        <a class="platform" role="link" :href="bookManifest.macosDmgSrc || undefined" :aria-disabled="!bookManifest.macosDmgSrc" :tabindex="bookManifest.macosDmgSrc ? undefined : -1" download aria-label="Скачать приложение для macOS" title="DMG для macOS Apple Silicon">
                                                                <span class="symbol macos" aria-hidden="true"></span>
                                                        </a>
                                                        <a class="platform" role="link" :href="bookManifest.androidApkSrc || undefined" :aria-disabled="!bookManifest.androidApkSrc" :tabindex="bookManifest.androidApkSrc ? undefined : -1" download aria-label="Скачать приложение для Android">
                                                                <span class="symbol android" aria-hidden="true"></span>
                                                        </a>
                                                        <a class="platform" role="link" :href="bookManifest.linuxAppImageSrc || undefined" :aria-disabled="!bookManifest.linuxAppImageSrc" :tabindex="bookManifest.linuxAppImageSrc ? undefined : -1" download aria-label="Скачать приложение для Linux" title="AppImage для Linux x86_64">
                                                                <span class="symbol linux" aria-hidden="true"></span>
                                                        </a>
                                                </div>
                                        </div>
                                </section>
                        </div>

                        <section class="projects" aria-labelledby="reader-book-overview-projects-title">
                                <h2 id="reader-book-overview-projects-title">ОБРАЗОВАТЕЛЬНЫЕ ПРОЕКТЫ «АСТ-ПРЕСС ШКОЛА»</h2>
                                <p>Цифровые образовательные решения для школы, учителей и учеников</p>
                                <div class="cards">
                                        <article class="card atlas">
                                                <div class="layout">
                                                        <div class="details">
                                                                <h3>Цифровой школьный атлас</h3>
                                                                <p>Интерактивные карты для изучения географии</p>
                                                                <Button class="action" type="button" variant="outlined" severity="secondary" label="Узнать подробнее" @click="externalLinkService.open({ url: 'https://school-atlas.ru/', source: 'book-overview-school-atlas' })" />
                                                        </div>
                                                        <div class="illustration" aria-hidden="true"></div>
                                                </div>
                                        </article>
                                        <article class="card native-land">
                                                <div class="layout">
                                                        <div class="details">
                                                                <h3>Край родной — моя Россия</h3>
                                                                <p>История, культура и география родного края</p>
                                                                <Button class="action" type="button" variant="outlined" severity="secondary" label="Узнать подробнее" @click="externalLinkService.open({ url: 'https://xn--j1am.xn--p1ai/', source: 'book-overview-native-land' })" />
                                                        </div>
                                                        <div class="illustration" aria-hidden="true"></div>
                                                </div>
                                        </article>
                                </div>
                        </section>
                </div>

                <ReaderSiteFooter />
        </section>
</template>

<script setup lang="ts">
        import "@/less/book-overview.less"
        import ReaderSiteFooter from "@/window/ReaderSiteFooter.vue"
        import { computed, ref } from "vue"
        import { storeToRefs } from "pinia"
        import { pStore } from "@/ts/storage/store"
        import { createExternalLinkService } from "@/ts/external-link-service"
        import { sessionUser } from "@/ts/api/auth-session"

        const props = defineProps<{
                hasReadingProgress: boolean
                resumeContext: string | null
                currentPageNumber: number | null
                bookmarkCount: number
                noteCount: number
                annotationsError: string
                isLoadingAnnotations: boolean
        }>()

        const { bookManifest, overview, overviewPageCount: totalPages, readingPageCount } = storeToRefs(pStore())
        const readingProgress = computed(() => readingPageCount.value > 0
                ? Math.min(100, (props.currentPageNumber ?? 0) / readingPageCount.value * 100)
                : 0)
        const access = computed(() => {
                if (!overview.value) return null
                return props.hasReadingProgress
                        ? {
                                ...overview.value.access,
                                title: "Продолжить чтение",
                                description: props.resumeContext ?? "Учебник откроется на сохранённой странице.",
                                actionLabel: "ПРОДОЛЖИТЬ ЧТЕНИЕ",
                        }
                        : overview.value.access
        })

        const emit = defineEmits<{
                (event: "open-reader"): void
        }>()

        const isDescriptionExpanded = ref(false)
        const externalLinkService = createExternalLinkService()
</script>
